import * as React from "react";

import { type PrefsOut, notificationsApi } from "@/api/notifications";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useUIStore } from "@/stores/ui";

type ChannelKey =
  | "email_enabled"
  | "in_app_enabled"
  | "teams_enabled"
  | "slack_enabled"
  | "zalo_enabled"
  | "sms_enabled";

const CHANNELS: Array<{ key: ChannelKey; label: string }> = [
  { key: "email_enabled", label: "Email" },
  { key: "in_app_enabled", label: "In-app (chuông)" },
  { key: "teams_enabled", label: "Microsoft Teams" },
  { key: "slack_enabled", label: "Slack" },
  { key: "zalo_enabled", label: "Zalo" },
  { key: "sms_enabled", label: "SMS" },
];

export function NotificationPreferences(): JSX.Element {
  const pushToast = useUIStore((s) => s.pushToast);
  const [prefs, setPrefs] = React.useState<PrefsOut | null>(null);
  const [quietFrom, setQuietFrom] = React.useState("");
  const [quietTo, setQuietTo] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    void (async () => {
      try {
        const p = await notificationsApi.getPrefs();
        setPrefs(p);
        if (p.quiet_hours) {
          setQuietFrom(String((p.quiet_hours as Record<string, string>).from ?? ""));
          setQuietTo(String((p.quiet_hours as Record<string, string>).to ?? ""));
        }
      } catch (e) {
        pushToast({
          variant: "destructive",
          title: "Lỗi tải prefs",
          description: e instanceof Error ? e.message : String(e),
        });
      }
    })();
  }, [pushToast]);

  if (!prefs) {
    return (
      <div className="space-y-3 max-w-2xl">
        <div className="shimmer h-6 w-1/3 rounded" />
        <div className="shimmer h-4 w-2/3 rounded" />
        <div className="shimmer h-32 w-full rounded-lg mt-4" />
        <div className="shimmer h-24 w-full rounded-lg" />
      </div>
    );
  }

  const toggle = async (key: ChannelKey): Promise<void> => {
    setBusy(true);
    try {
      const cur = Boolean(prefs[key]);
      const next = await notificationsApi.updatePrefs({ [key]: !cur });
      setPrefs(next);
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lỗi",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  const saveQuiet = async (): Promise<void> => {
    const valid = /^([01]\d|2[0-3]):[0-5]\d$/;
    if (quietFrom && !valid.test(quietFrom)) {
      pushToast({ variant: "warning", title: "from phải HH:MM" });
      return;
    }
    if (quietTo && !valid.test(quietTo)) {
      pushToast({ variant: "warning", title: "to phải HH:MM" });
      return;
    }
    setBusy(true);
    try {
      const body = {
        quiet_hours:
          quietFrom && quietTo
            ? { from: quietFrom, to: quietTo, timezone: "Asia/Ho_Chi_Minh" }
            : null,
      };
      const next = await notificationsApi.updatePrefs(body);
      setPrefs(next);
      pushToast({ variant: "success", title: "Đã lưu quiet hours" });
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Lưu thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-4">
      <header>
        <h1 className="text-xl font-semibold">Tuỳ chọn thông báo</h1>
        <p className="text-sm text-muted-foreground">
          Bật/tắt từng kênh, đặt giờ im lặng. Áp dụng cho tài khoản của bạn.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Channels</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {CHANNELS.map((c) => (
            <label
              key={c.key}
              className="flex items-center gap-3 text-sm py-1 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={Boolean(prefs[c.key])}
                onChange={() => void toggle(c.key)}
                disabled={busy}
                className="size-4"
              />
              {c.label}
            </label>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Quiet hours</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Trong khoảng này, thông báo sẽ chờ đến hết quiet rồi mới gửi
            (Asia/Ho_Chi_Minh). Bỏ trống để tắt.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-medium block mb-1">From (HH:MM)</label>
              <Input
                value={quietFrom}
                onChange={(e) => setQuietFrom(e.target.value)}
                placeholder="22:00"
              />
            </div>
            <div>
              <label className="text-xs font-medium block mb-1">To (HH:MM)</label>
              <Input
                value={quietTo}
                onChange={(e) => setQuietTo(e.target.value)}
                placeholder="07:00"
              />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={() => void saveQuiet()} disabled={busy}>
              {busy ? "..." : "Lưu quiet hours"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
